import { useState, useEffect } from 'react'
import './CacheInspector.css'

interface CacheEntry {
  url: string
  size: number
  timestamp: number
}

interface CacheStats {
  totalTiles: number
  totalSize: number
  osmTiles: number
  niwaTiles: number
  fontGlyphs: number
  entries: CacheEntry[]
}

const CacheInspector = () => {
  const [stats, setStats] = useState<CacheStats | null>(null)
  const [isOpen, setIsOpen] = useState(false)
  const [isLoading, setIsLoading] = useState(false)

  const loadCacheStats = async () => {
    setIsLoading(true)
    try {
      const cache = await caches.open('runtime-cache-v1')
      const requests = await cache.keys()
      
      let totalSize = 0
      let osmCount = 0
      let niwaCount = 0
      let fontCount = 0
      const entries: CacheEntry[] = []

      for (const request of requests) {
        const response = await cache.match(request)
        if (response) {
          const blob = await response.blob()
          const size = blob.size
          totalSize += size
          
          const url = request.url
          entries.push({
            url,
            size,
            timestamp: Date.now() // Actual timestamp would need to be stored separately
          })

          if (url.includes('openstreetmap.org')) osmCount++
          else if (url.includes('niwa.co.nz')) niwaCount++
          else if (url.includes('fonts') || url.includes('glyphs')) fontCount++
        }
      }

      setStats({
        totalTiles: requests.length,
        totalSize,
        osmTiles: osmCount,
        niwaTiles: niwaCount,
        fontGlyphs: fontCount,
        entries: entries.sort((a, b) => b.size - a.size).slice(0, 100) // Top 100
      })
    } catch (error) {
      console.error('Failed to load cache stats:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const clearCache = async () => {
    if (!confirm('Clear all cached tiles? You will need to re-download them.')) {
      return
    }
    
    try {
      await caches.delete('runtime-cache-v1')
      setStats(null)
      alert('Cache cleared successfully!')
    } catch (error) {
      console.error('Failed to clear cache:', error)
      alert('Failed to clear cache')
    }
  }

  const formatBytes = (bytes: number) => {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i]
  }

  const exportCacheList = () => {
    if (!stats) return
    
    const data = JSON.stringify(stats, null, 2)
    const blob = new Blob([data], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `cache-export-${new Date().toISOString().split('T')[0]}.json`
    a.click()
    URL.revokeObjectURL(url)
  }

  useEffect(() => {
    if (isOpen && !stats) {
      loadCacheStats()
    }
  }, [isOpen])

  return (
    <>
      <button 
        className="cache-inspector-toggle"
        onClick={() => setIsOpen(!isOpen)}
        title="View cached tiles"
      >
        💾
      </button>

      {isOpen && (
        <div className="cache-inspector">
          <div className="cache-inspector-header">
            <h3>Cached Tiles</h3>
            <button className="close-btn" onClick={() => setIsOpen(false)}>✖️</button>
          </div>

          {isLoading && <div className="loading">Loading cache stats...</div>}

          {stats && (
            <>
              <div className="cache-stats">
                <div className="stat-item">
                  <span className="stat-label">Total Tiles:</span>
                  <span className="stat-value">{stats.totalTiles.toLocaleString()}</span>
                </div>
                <div className="stat-item">
                  <span className="stat-label">Total Size:</span>
                  <span className="stat-value">{formatBytes(stats.totalSize)}</span>
                </div>
                <div className="stat-item">
                  <span className="stat-label">OSM Tiles:</span>
                  <span className="stat-value">{stats.osmTiles.toLocaleString()}</span>
                </div>
                <div className="stat-item">
                  <span className="stat-label">NIWA Tiles:</span>
                  <span className="stat-value">{stats.niwaTiles.toLocaleString()}</span>
                </div>
                <div className="stat-item">
                  <span className="stat-label">Font Glyphs:</span>
                  <span className="stat-value">{stats.fontGlyphs.toLocaleString()}</span>
                </div>
              </div>

              <div className="cache-actions">
                <button onClick={loadCacheStats} className="refresh-btn">
                  🔄 Refresh
                </button>
                <button onClick={exportCacheList} className="export-btn">
                  📋 Export List
                </button>
                <button onClick={clearCache} className="clear-btn">
                  🗑️ Clear Cache
                </button>
              </div>

              <div className="cache-hint">
                <strong>💡 Tip:</strong> Tiles are stored in browser IndexedDB.<br/>
                Open DevTools → Application → Cache Storage → runtime-cache-v1
              </div>

              <div className="cache-entries">
                <h4>Largest Cached Items (Top 100)</h4>
                <div className="entries-list">
                  {stats.entries.map((entry, i) => (
                    <div key={i} className="entry-item">
                      <span className="entry-url" title={entry.url}>
                        {new URL(entry.url).pathname.split('/').pop()}
                      </span>
                      <span className="entry-size">{formatBytes(entry.size)}</span>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </>
  )
}

export default CacheInspector
